import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Check, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rules = [
  { key: "length", label: "Au moins 8 caractères", test: (p) => p.length >= 8 },
  { key: "lower", label: "Une minuscule", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "Une majuscule", test: (p) => /[A-Z]/.test(p) },
  { key: "digit", label: "Un chiffre", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "Un caractère spécial", test: (p) => /[^A-Za-z0-9]/.test(p) }
];

export default function ChangePassword() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(API_URL + "/api/auth/me", { credentials: "include" });
        if (!res.ok) { navigate("/login", { replace: true }); return; }
      } catch (e) { navigate("/login", { replace: true }); return; }
      setChecking(false);
    })();
  }, [navigate]);

  const allValid = rules.every((r) => r.test(newPassword));
  const matches = newPassword.length > 0 && newPassword === confirmPassword;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!allValid) { toast.error("Le mot de passe ne respecte pas les critères"); return; }
    if (!matches) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      const res = await fetch(API_URL + "/api/auth/change-password", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_password: newPassword })
      });
      if (res.ok) {
        toast.success("Mot de passe modifié avec succès");
        navigate("/dashboard", { replace: true });
      } else {
        const err = await res.json();
        toast.error(typeof err.detail === "string" ? err.detail : "Erreur");
      }
    } catch (e) {
      toast.error("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6" data-testid="change-password-page">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="font-heading text-2xl">Définir votre mot de passe</CardTitle>
            <CardDescription>
              Pour votre sécurité, vous devez choisir un nouveau mot de passe fort avant de continuer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <div className="rounded-lg bg-muted/50 p-3 space-y-1.5" data-testid="password-rules">
                {rules.map((r) => {
                  const ok = r.test(newPassword);
                  return (
                    <div key={r.key} className="flex items-center gap-2 text-xs">
                      {ok ? <Check className="w-3.5 h-3.5 text-green-600" /> : <X className="w-3.5 h-3.5 text-slate-300" />}
                      <span className={ok ? "text-green-700" : "text-muted-foreground"}>{r.label}</span>
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 text-xs">
                  {matches ? <Check className="w-3.5 h-3.5 text-green-600" /> : <X className="w-3.5 h-3.5 text-slate-300" />}
                  <span className={matches ? "text-green-700" : "text-muted-foreground"}>Les mots de passe correspondent</span>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !allValid || !matches}
                data-testid="change-password-submit-btn"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer et continuer
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
